import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { ArrowLeft, Calendar as CalendarIcon, Clock, User, Mail, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const BookingPage = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    if (selectedService && selectedDate) {
      loadAvailableSlots();
    }
  }, [selectedService, selectedDate]);

  const loadServices = async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Error al cargar servicios');
    }
  };

  const loadAvailableSlots = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/appointments/available-slots`, {
        params: { date: dateStr, service_id: selectedService.id }
      });
      setAvailableSlots(res.data.available_slots);
      setSelectedTime('');
    } catch (error) {
      console.error('Error loading slots:', error);
      toast.error('Error al cargar horarios disponibles');
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setStep(2);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    if (date) {
      setStep(3);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService || !selectedDate || !selectedTime || !clientName || !clientEmail || !clientPhone) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(`${API}/appointments`, {
        service_id: selectedService.id,
        client_name: clientName,
        client_email: clientEmail,
        client_phone: clientPhone,
        date: dateStr,
        time: selectedTime
      });

      toast.success('¡Solicitud de cita enviada! Recibirás un email cuando sea confirmada por nuestro equipo.');
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error(error.response?.data?.detail || 'Error al reservar la cita');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    }
    return `${minutes} min`;
  };

  const disabledDays = [
    { dayOfWeek: [0] }, // Sundays
    { before: new Date() }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100 py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')} 
          className="mb-6"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al inicio
        </Button>

        <Card className="shadow-2xl border-2 border-pink-100 bg-white/95 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-3xl sm:text-4xl text-center mb-2">Reserva tu Cita</CardTitle>
            <CardDescription className="text-center text-base">Completa los siguientes pasos para agendar tu visita</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {/* Progress Steps */}
            <div className="flex items-center justify-center mb-8">
              {[1, 2, 3, 4].map((num) => (
                <div key={num} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    step >= num ? 'bg-pink-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {num}
                  </div>
                  {num < 4 && <div className={`w-12 h-1 ${
                    step > num ? 'bg-pink-500' : 'bg-gray-200'
                  }`}></div>}
                </div>
              ))}
            </div>

            {/* Step 1: Select Service */}
            {step >= 1 && (
              <div className="mb-8" data-testid="step-select-service">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-pink-100 text-pink-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                  Selecciona un Servicio
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {services.map((service, index) => (
                    <button
                      key={service.id}
                      onClick={() => handleServiceSelect(service)}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        selectedService?.id === service.id
                          ? 'border-pink-500 bg-pink-50 shadow-lg'
                          : 'border-gray-200 hover:border-pink-300 hover:shadow-md'
                      }`}
                      data-testid={`select-service-${index}`}
                    >
                      <h4 className="font-semibold text-lg mb-1">{service.name}</h4>
                      <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                      <div className="flex items-center text-pink-600 text-sm font-medium">
                        <Clock className="mr-1 h-4 w-4" />
                        {formatDuration(service.duration_minutes)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Select Date */}
            {step >= 2 && selectedService && (
              <div className="mb-8" data-testid="step-select-date">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-pink-100 text-pink-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Selecciona una Fecha
                </h3>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800 text-center">
                    <Clock className="inline h-4 w-4 mr-1" />
                    Todos los horarios están en zona horaria de Miami Dade (Eastern Time)
                  </p>
                </div>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={disabledDays}
                    locale={es}
                    className="rounded-lg border-2 border-pink-100 bg-white p-3"
                    data-testid="booking-calendar"
                  />
                </div>
                {selectedDate && (
                  <p className="text-center mt-4 text-lg font-medium text-pink-600">
                    Fecha seleccionada: {format(selectedDate, 'PPP', { locale: es })}
                  </p>
                )}
              </div>
            )}

            {/* Step 3: Select Time */}
            {step >= 3 && selectedDate && (
              <div className="mb-8" data-testid="step-select-time">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-pink-100 text-pink-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  Selecciona un Horario
                </h3>
                {availableSlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot}
                        onClick={() => {
                          setSelectedTime(slot);
                          setStep(4);
                        }}
                        className={`p-3 rounded-lg border-2 transition-all font-medium ${
                          selectedTime === slot
                            ? 'border-pink-500 bg-pink-500 text-white'
                            : 'border-gray-200 hover:border-pink-300 hover:bg-pink-50'
                        }`}
                        data-testid={`time-slot-${slot}`}
                      >
                        {slot}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">No hay horarios disponibles para esta fecha. Por favor selecciona otra fecha.</p>
                )}
              </div>
            )}

            {/* Step 4: Client Information */}
            {step >= 4 && selectedTime && (
              <div data-testid="step-client-info">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <span className="bg-pink-100 text-pink-600 w-8 h-8 rounded-full flex items-center justify-center text-sm">4</span>
                  Información de Contacto
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4" />
                      Nombre Completo
                    </Label>
                    <Input
                      id="name"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      required
                      placeholder="Tu nombre"
                      className="border-2 border-gray-200 focus:border-pink-300"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      required
                      placeholder="tu@email.com"
                      className="border-2 border-gray-200 focus:border-pink-300"
                      data-testid="client-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="flex items-center gap-2 mb-2">
                      <Phone className="h-4 w-4" />
                      Teléfono
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      required
                      placeholder="+1 234 567 890"
                      className="border-2 border-gray-200 focus:border-pink-300"
                      data-testid="client-phone-input"
                    />
                  </div>

                  {/* Summary */}
                  <div className="bg-pink-50 border-2 border-pink-200 rounded-xl p-6 mt-6">
                    <h4 className="font-semibold text-lg mb-3 text-pink-800">Resumen de tu Cita</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Servicio:</strong> {selectedService.name}</p>
                      <p><strong>Fecha:</strong> {format(selectedDate, 'PPP', { locale: es })}</p>
                      <p><strong>Hora:</strong> {selectedTime}</p>
                      <p><strong>Duración:</strong> {formatDuration(selectedService.duration_minutes)}</p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white py-6 text-lg rounded-full shadow-xl"
                    data-testid="confirm-booking-btn"
                  >
                    {loading ? 'Reservando...' : 'Confirmar Reserva'}
                  </Button>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingPage;